import React, { useCallback, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { useSetRecoilState } from 'recoil';
import { v4 as uuidv4 } from 'uuid';
import { Button, Modal } from 'up-ops-web-shared-components/lib';
import RadioButton from 'up-ops-web-shared-components/lib/components/radio/radio-button';
import StyledDiv from 'up-ops-web-shared-components/lib/components/styled-div';

import FormHeader from 'components/FormHeader';
import FileDownloadLink from 'components/FileDownloadLink';
import { FormRow, UploadHint, UploadHintList } from 'styles/CommonStyles';
import FormFileUpload from 'components/FormFileUpload';
import FormTextInput from 'components/FormTextInput';
import FormMonetaryInput from 'components/FormMonetaryInput';
import FormDatePicker from 'components/FormDatePicker';
import FormSubmit from 'components/FormSubmit';
import FormTracker from 'components/FormTracker';
import { FullWidthTextArea } from 'components/FormTextArea';
import { TwoAnswerRadioGroup } from 'components/FormRadioGroup';
import useSubmitActions from 'hooks/submit';
import useUploadActions from 'hooks/upload';
import { FormIdentifier } from 'lib/constants';
import { normalizePhoneNumber } from 'lib/utils';
import { patterns } from 'lib/validation';
import { showSubmitSuccessState } from 'state/atoms/submit';
import { acceptedFiles } from 'lib/constants';
const CardTheftForm = () => {
  const { i18n, t } = useTranslation();
  const { submitForm } = useSubmitActions();
  const { uploadFiles } = useUploadActions();
  const formMethods = useForm();
  const setShowSuccess = useSetRecoilState(showSubmitSuccessState);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const onSubmit = useCallback(
    (data: any) => {
      const { documents, lastChargeAmount, phoneNumber, ...otherData } = data;
      const formData = {
        formType: FormIdentifier.CARD_THEFT,
        lastChargeAmount: lastChargeAmount * 100,
        phoneNumber: normalizePhoneNumber(phoneNumber),
        ...otherData,
      };
      const formSessionUuid = uuidv4();

      return uploadFiles(formSessionUuid, 'fraud', documents)
        .then((uploadedDocs) =>
          submitForm(
            formSessionUuid,
            { ...formData, documents: uploadedDocs },
            i18n.language
          )
        )
        .then(() => setShowSuccess(true));
    },
    [i18n.language, setShowSuccess, submitForm, uploadFiles]
  );

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={formMethods.handleSubmit(onSubmit)}>
        <FormHeader
          description={t('forms.cardTheft.description')}
          title={t('forms.cardTheft.name')}
        />
        <StyledDiv marginVertical="l">
          <FileDownloadLink
            hint={t('common.optionalHint')}
            href="/downloads/Affidavit of Unauthorized Card Activity.pdf"
            text={t('forms.cardTheft.downloadUnauthForm')}
          />
        </StyledDiv>
        <FormRow>
          <FormTextInput
            label={t<string>('labels.name')}
            name="name"
            rules={{
              pattern: {
                value: patterns.noSpecialCharacters,
                message: t<string>('validation.invalid'),
              },
              required: t<string>('validation.required'),
            }}
          />
          <FormTextInput
            label={t<string>('labels.emailAddress')}
            name="email"
            rules={{
              pattern: {
                message: t<string>('validation.emailFormat'),
                value: patterns.emailAddress,
              },
              required: t<string>('validation.required'),
            }}
          />
        </FormRow>
        <FormRow>
          <FormTextInput
            label={t<string>('labels.phoneNumber')}
            name="phoneNumber"
            rules={{
              pattern: {
                message: t<string>('validation.phoneNumberFormat'),
                value: patterns.phoneNumber,
              },
              required: t<string>('validation.required'),
            }}
          />
          <FormTextInput
            label={t<string>('labels.cardDigits')}
            name="lastFourDigitsOfCard"
            rules={{
              pattern: {
                message: t<string>('validation.cardDigitsFormat'),
                value: patterns.lastFourDigits,
              },
              required: t<string>('validation.required'),
            }}
          />
        </FormRow>
        <FormRow>
          <FormMonetaryInput
            allowDecimal
            label={t<string>('labels.lastTransactionAmount')}
            name="lastChargeAmount"
            rules={{ required: t<string>('validation.required') }}
          />
          <FormDatePicker
            label={t<string>('labels.lastTransactionDate')}
            name="lastChargeDate"
            rules={{ required: t<string>('validation.required') }}
          />
        </FormRow>
        <FormRow>
          <TwoAnswerRadioGroup
            name="policeReportFiled"
            rules={{
              required: t<string>('validation.policeReportFilingRequired'),
            }}
            title={t('labels.policeReportFiling')}
          >
            <RadioButton label={t<string>('common.yes')} value="yes" />
            <RadioButton
              label={t<string>('common.no')}
              value="no"
              onChange={() => setIsModalOpen(true)}
            />
          </TwoAnswerRadioGroup>
          {isModalOpen && (
            <Modal
              modalTitle=""
              modalFooter={
                <Button onClick={() => setIsModalOpen(false)}>
                  {t('common.close')}
                </Button>
              }
            >
              {t('prompts.policeReportWarning')}
            </Modal>
          )}
        </FormRow>
        <FormRow>
          <FullWidthTextArea
            label={t<string>('labels.unauthorizedActivityDescription')}
            name="activitySummary"
          />
        </FormRow>
        <FormRow>
          <div>
            <FormFileUpload
              acceptTypes={acceptedFiles}
              ariaLabel={t('labels.uploadDocuments')}
              label={
                <>
                  {t('labels.uploadDocuments')}
                  <UploadHint>
                    <Trans i18nKey="labels.uploadFollowingDocuments" />
                    <UploadHintList>
                      <li>{t('labels.unauthActivityForm')}</li>
                      <li>{t('labels.policeReport')}</li>
                    </UploadHintList>
                  </UploadHint>
                </>
              }
              name="documents"
              maxFiles={2}
              rules={{
                required: t<string>('validation.uploadDocumentsRequired'),
              }}
            />
          </div>
        </FormRow>
        <FormRow>
          <FullWidthTextArea
            label={t<string>('labels.comments')}
            name="comments"
          />
        </FormRow>
        <FormSubmit formType={FormIdentifier.CARD_THEFT} />
        <FormTracker formType={FormIdentifier.CARD_THEFT} />
      </form>
    </FormProvider>
  );
};

export default CardTheftForm;
